import React, { useState, useEffect, memo } from 'react';
import { Tabs, Tab, Box, Button, Paper, Typography, Grid, TextField, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import bg from '../assets/banner.jpg';
import {
  listCompanies, createCompany, updateCompany, deleteCompany,
  listInsCompanies, createInsCompany, updateInsCompany, deleteInsCompany,
  listAgents, createAgent, updateAgent, deleteAgent,
  listRiskItems, createRiskItem, updateRiskItem, deleteRiskItem,
  listBankers, createBanker, updateBanker, deleteBanker
} from '../api';

// Moved FormComponent outside DataPage and memoized it
const FormComponent = memo(({ fields, formName, formData, setFormData, editingId, setEditingId, fetchData }) => {
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (editingId) {
        // Update existing record
        switch (formName) {
          case 'CoDetail':
            response = await updateCompany(editingId, formData);
            break;
          case 'InsCompany':
            response = await updateInsCompany(editingId, formData);
            break;
          case 'Agent':
            response = await updateAgent(editingId, formData);
            break;
          case 'Risk Items':
            response = await updateRiskItem(editingId, formData);
            break;
          case 'Financer':
            response = await updateBanker(editingId, formData);
            break;
          default:
            throw new Error('Invalid form name');
        }
        alert(`${formName} updated successfully`);
      } else {
        // Create new record
        switch (formName) {
          case 'CoDetail':
            response = await createCompany(formData);
            break;
          case 'InsCompany':
            response = await createInsCompany(formData);
            break;
          case 'Agent':
            response = await createAgent(formData);
            break;
          case 'Risk Items':
            response = await createRiskItem(formData);
            break;
          case 'Financer':
            response = await createBanker(formData);
            break;
          default:
            throw new Error('Invalid form name');
        }
        alert(`${formName} created successfully`);
      }
      setFormData({}); // Clear form
      setEditingId(null); // Reset editing mode
      fetchData(formName); // Refresh table data
    } catch (error) {
      console.error('Form submission failed:', error.message);
      alert(`Failed to ${editingId ? 'update' : 'create'} ${formName}: ${error.message}`);
    }
  };

  const rows = {};
  fields.forEach((field) => {
    if (!rows[field.row]) rows[field.row] = [];
    rows[field.row].push(field);
  });

  const firstRowLength = rows['row1'] ? rows['row1'].length : 1;

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={2} direction="column">
        {Object.entries(rows).map(([rowKey, rowFields]) => (
          <Grid container item spacing={2} key={rowKey} alignItems="center">
            {rowFields.map((field) => (
              <Grid item xs={12} sm={12 / Math.min(rowFields.length, 5)} key={field.name}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label={field.label + (field.optional ? ' (Optional)' : '')}
                  name={field.name}
                  type={field.type || 'text'}
                  value={formData[field.name] || ''}
                  onChange={handleChange}
                  sx={{
                    '& label.Mui-focused': { color: '#e0b256' },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#ccc' },
                      '&:hover fieldset': { borderColor: '#e0b256' },
                      '&.Mui-focused fieldset': { borderColor: '#e0b256' },
                    },
                    color: '#fff',
                    backgroundColor: '#1e1e1e',
                  }}
                  InputProps={{ sx: { py: 1, color: '#fff' } }}
                  InputLabelProps={field.type === 'date' ? { shrink: true } : {}}
                />
              </Grid>
            ))}
          </Grid>
        ))}
        <Grid container item spacing={2} alignItems="center">
          <Grid item xs={12} sm={12 / Math.min(firstRowLength, 5)}>
            <Button
              variant="contained"
              type="submit"
              sx={{
                py: 1.5,
                mt: 2,
                px: 11,
                fontWeight: 'bold',
                backgroundColor: '#e0b256',
                color: '#000',
                '&:hover': { backgroundColor: '#c99e3c' },
                width: '100%',
              }}
            >
              {editingId ? 'UPDATE' : 'SUBMIT'}
            </Button>
          </Grid>
          {editingId && (
            <Grid item xs={12} sm={12 / Math.min(firstRowLength, 5)}>
              <Button
                variant="outlined"
                onClick={() => {
                  setEditingId(null);
                  setFormData({});
                }}
                sx={{
                  py: 1.5,
                  mt: 2,
                  px: 11,
                  fontWeight: 'bold',
                  color: '#fff',
                  borderColor: '#fff',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                  width: '100%',
                }}
              >
                CANCEL EDIT
              </Button>
            </Grid>
          )}
        </Grid>
      </Grid>
    </form>
  );
});

function DataPage() {
  const navigate = useNavigate();
  const [value, setValue] = useState(0);
  const [searchTerms, setSearchTerms] = useState({
    CoDetail: '',
    InsCompany: '',
    Agent: '',
    'Risk Items': '',
    Financer: '',
  });
  const [data, setData] = useState({
    CoDetail: [],
    InsCompany: [],
    Agent: [],
    'Risk Items': [],
    Financer: [],
  });
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({});

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setEditingId(null); // Reset editing mode when switching tabs
    setFormData({}); // Clear form data
  };

const handleSearchChange = (formName, value) => {
  setSearchTerms(prev => ({
    ...prev,
    [formName]: value
  }));
};


  const fetchData = async (formName) => {
    try {
      let result;
      switch (formName) {
        case 'CoDetail':
          result = await listCompanies();
          break;
        case 'InsCompany':
          result = await listInsCompanies();
          break;
        case 'Agent':
          result = await listAgents();
          break;
        case 'Risk Items':
          result = await listRiskItems();
          break;
        case 'Financer':
          result = await listBankers();
          break;
        default:
          return;
      }
      setData((prev) => ({ ...prev, [formName]: result }));
    } catch (error) {
      console.error(`Failed to fetch ${formName} data:`, error.message);
      alert(`Failed to fetch ${formName} data: ${error.message}`);
    }
  };

  useEffect(() => {
    const formName = Object.keys(formFields)[value];
    fetchData(formName);
  }, [value]);

  const formFields = {
    CoDetail: [
      { label: 'Legal Name', name: 'legal_name', row: 'row1' },
      { label: 'Company Name', name: 'company_name', row: 'row1' },
      { label: 'GST', name: 'gst', row: 'row1' },
      { label: 'PAN', name: 'pan', row: 'row1' },
      { label: 'Company Email', name: 'company_email', row: 'row1' },
      { label: 'Street Address', name: 'street_address', row: 'row2' },
      { label: 'City', name: 'city', row: 'row2' },
      { label: 'State', name: 'state', row: 'row2' },
      { label: 'Country', name: 'country', row: 'row2' },
      { label: 'Contact Person Name', name: 'contact_person_name', row: 'row3' },
      { label: 'Contact Person Email', name: 'contact_person_email', row: 'row3' },
      { label: 'Contact Person Phone', name: 'contact_person_phone', row: 'row3' },
    ],
    InsCompany: [
      { label: 'Company Name', name: 'company_name', row: 'row1' },
      { label: 'Legal Name', name: 'legal_name', row: 'row1' },
      { label: 'Website', name: 'website', row: 'row1' },
      { label: 'Street Address', name: 'street_address', row: 'row2' },
      { label: 'City', name: 'city', row: 'row2' },
      { label: 'State', name: 'state', row: 'row2' },
      { label: 'Country', name: 'country', row: 'row2' },
      { label: 'Customer Care Email', name: 'customer_care_email', row: 'row3' },
      { label: 'Customer Care Toll-Free', name: 'customer_care_tollfree', row: 'row3' },
    ],
    Agent: [
      { label: 'Name', name: 'name', row: 'row1' },
      { label: 'Legal Name', name: 'legal_name', row: 'row1' },
      { label: 'GST', name: 'gst', row: 'row1' },
      { label: 'PAN', name: 'pan', row: 'row1' },
      { label: 'Company Email', name: 'company_email', row: 'row1' },
      { label: 'Street Address', name: 'street_address', row: 'row2' },
      { label: 'City', name: 'city', row: 'row2' },
      { label: 'State', name: 'state', row: 'row2' },
      { label: 'Country', name: 'country', row: 'row2' },
      { label: 'Contact Person Name', name: 'contact_person_name', row: 'row3' },
      { label: 'Contact Person Email', name: 'contact_person_email', row: 'row3' },
      { label: 'Contact Person Phone', name: 'contact_person_phone', row: 'row3' },
    ],
    'Risk Items': [
      { label: 'Name', name: 'name', row: 'row1' },
      { label: 'Created At', name: 'created_at', row: 'row1', type: 'date' },
      { label: 'Created By', name: 'created_by', row: 'row1' },
      { label: 'User Reference', name: 'user_reference', row: 'row2' },
      { label: 'Date of Creation', name: 'date_of_creation', type: 'date', row: 'row2' },
    ],
    Financer: [
      { label: 'Financer Name', name: 'banker_name', row: 'row1' },
      { label: 'Contact', name: 'contact', optional: true, row: 'row2' },
      { label: 'Branch', name: 'branch', optional: true, row: 'row2' },
      { label: 'Address', name: 'address', optional: true, row: 'row2' },
      { label: 'Mail', name: 'mail', optional: true, row: 'row2' },
    ],
  };

  const tableColumns = {
    CoDetail: ['Legal Name', 'Company Name', 'GST', 'PAN', 'Company Email', 'Street Address', 'City', 'State', 'Country', 'Contact Person Name'],
    InsCompany: ['Company Name', 'Legal Name', 'Website', 'Street Address', 'City', 'State', 'Country', 'Customer Care Email'],
    Agent: ['Name', 'Legal Name', 'GST', 'PAN', 'Company Email', 'Street Address', 'City', 'State', 'Country', 'Contact Person Name'],
    'Risk Items': ['Name', 'Created At', 'Created By', 'User Reference', 'Date of Creation'],
    Financer: ['Financer Name', 'Contact', 'Branch', 'Address', 'Mail'],
  };

const TableComponent = ({ columns, data, formName, searchTerm, handleSearchChange }) => {

    const filteredData = data.filter((row) => {
      const searchTerm = searchTerms[formName].toLowerCase();
      return columns.some((col) =>
        String(
          col === 'Created By' && row.created_by && typeof row.created_by === 'object'
            ? row.created_by.username || 'N/A'
            : row[col.toLowerCase().replace(/ /g, '_')]
        ).toLowerCase().includes(searchTerm)
      );
    });

    const handleEdit = (row) => {
      setFormData({
        ...row,
        created_by:
          row.created_by && typeof row.created_by === 'object'
            ? row.created_by.username
            : row.created_by || '',
      });
      setEditingId(row._id); // Use _id from MongoDB
    };

    const handleDelete = async (id) => {
      if (window.confirm(`Are you sure you want to delete this ${formName} record?`)) {
        try {
          switch (formName) {
            case 'CoDetail':
              await deleteCompany(id);
              break;
            case 'InsCompany':
              await deleteInsCompany(id);
              break;
            case 'Agent':
              await deleteAgent(id);
              break;
            case 'Risk Items':
              await deleteRiskItem(id);
              break;
            case 'Financer':
              await deleteBanker(id);
              break;
            default:
              throw new Error('Invalid form name');
          }
          alert(`${formName} deleted successfully`);
          fetchData(formName); // Refresh table data
        } catch (error) {
          console.error(`Failed to delete ${formName}:`, error.message);
          alert(`Failed to delete ${formName}: ${error.message}`);
        }
      }
    };

    return (
      <>
       {/* <TextField
  placeholder={`Search ${formName}`}
  value={searchTerm || ''}
  onChange={(e) => handleSearchChange(formName, e.target.value)}
  fullWidth
  sx={{
    mb: 2,
    input: { color: '#fff' },
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#1e1e1e',
      borderRadius: 4,
      '& fieldset': { borderColor: '#ccc' },
      '&:hover fieldset': { borderColor: '#e0b256' },
      '&.Mui-focused fieldset': { borderColor: '#e0b256' },
    },
  }}
/> */}


        <Table>
          <TableHead>
            <TableRow>
              {columns.map((col, idx) => (
                <TableCell key={idx} sx={{ color: '#ccc' }}>{col}</TableCell>
              ))}
              <TableCell sx={{ color: '#ccc' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.length > 0 ? (
              filteredData.map((row) => (
                <TableRow key={row._id} sx={{ '&:hover': { backgroundColor: '#333' } }}>
                  {columns.map((col, idx) => (
                    <TableCell key={idx} sx={{ color: '#fff' }}>
                      {col === 'Created By' && row.created_by && typeof row.created_by === 'object'
                        ? row.created_by.username || 'N/A'
                        : row[col.toLowerCase().replace(/ /g, '_')] || 'N/A'}
                    </TableCell>
                  ))}
                  <TableCell>
                    <Button
                      variant="outlined"
                      onClick={() => handleEdit(row)}
                      style={{width:77}}
                      sx={{
                        mr: 1,
                        mb:1,
                        mt:1,
                       
                        color: '#e0b256',
                        borderColor: '#e0b256',
                        '&:hover': { backgroundColor: 'rgba(224, 178, 86, 0.1)' },
                      }}
                    >
                      Edit
                    </Button>
                    <></>
                    <Button
                      variant="outlined"
                      onClick={() => handleDelete(row._id)}
                      sx={{
                       
                        color: '#ff4444',
                        borderColor: '#ff4444',
                        '&:hover': { backgroundColor: 'rgba(255, 68, 68, 0.1)' },
                      }}
                    >
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length + 1} sx={{ color: '#ccc' }}>
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </>
    );
  };

  return (
    <Box display="flex">
      <Sidebar />
      <Box
        flexGrow={1}
        ml={30}
        sx={{
          minHeight: '100vh',
          backgroundImage: `url(${bg})`,
          backgroundAttachment: 'fixed',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          p: 4,
          color: '#fff',
          overflowY: 'auto',
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          textColor="inherit"
          TabIndicatorProps={{
            style: { backgroundColor: '#e0b256' },
          }}
          sx={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            borderRadius: 2,
            mb: 3,
          }}
        >
          <Tab label="Company" />
          <Tab label="Insurance" />
          <Tab label="Agent" />
          <Tab label="Risk Items" />
          <Tab label="Financer" />
        </Tabs>

        <Box
          sx={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            borderRadius: '2',
            p: 3,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: '#121212',
              color: '#fff',
              mb: 3,
            }}
          >
            <Typography variant="h6" gutterBottom>
              {value === 0 && 'Company Details'}
              {value === 1 && 'Insurance Company'}
              {value === 2 && 'Agent Details'}
              {value === 3 && 'Risk Items'}
              {value === 4 && 'Financer Details'}
            </Typography>
            <FormComponent
              fields={formFields[Object.keys(formFields)[value]]}
              formName={Object.keys(formFields)[value]}
              formData={formData}
              setFormData={setFormData}
              editingId={editingId}
              setEditingId={setEditingId}
              fetchData={fetchData}
            />
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              backgroundColor: '#121212',
              color: '#fff',
            }}
          >
            <Typography variant="h6" gutterBottom>
              {value === 0 && 'Company Details List'}
              {value === 1 && 'Insurance Company List'}
              {value === 2 && 'Agent Details List'}
              {value === 3 && 'Risk Items List'}
              {value === 4 && 'Financer Details List'}
            </Typography>
          <TableComponent
  columns={tableColumns[Object.keys(tableColumns)[value]]}
  data={data[Object.keys(formFields)[value]]}
  formName={Object.keys(formFields)[value]}
  searchTerm={searchTerms[Object.keys(formFields)[value]]}
  handleSearchChange={handleSearchChange}
/>

          </Paper>
        </Box>

        <Button
          variant="outlined"
          onClick={() => navigate('/dashboard')}
          sx={{
            mt: 3,
            color: '#fff',
            borderColor: '#fff',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
              borderColor: '#fff',
            },
          }}
        >
          Back to Dashboard
        </Button>
      </Box>
    </Box>
  );
}

export default DataPage;